import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Eye, EyeOff, ExternalLink, Info, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'
import { paymentGatewaysApi } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { GatewayType, CreatePaymentGatewaySettingsRequest } from '@/types'

// Gateway configuration with instructions
const GATEWAY_CONFIG: Record<GatewayType, {
  label: string
  description: string
  dashboardUrl: string
  docsUrl: string
  fields: {
    api_key: { label: string; placeholder: string; hint: string }
    api_secret: { label: string; placeholder: string; hint: string }
    webhook_secret: { label: string; placeholder: string; hint: string }
  }
  instructions: string[]
}> = {
  STRIPE: {
    label: 'Stripe',
    description: 'Accept credit card payments worldwide with Stripe',
    dashboardUrl: 'https://dashboard.stripe.com/apikeys',
    docsUrl: 'https://stripe.com/docs/keys',
    fields: {
      api_key: {
        label: 'Publishable Key',
        placeholder: 'pk_live_... or pk_test_...',
        hint: 'Found in Dashboard → Developers → API Keys'
      },
      api_secret: {
        label: 'Secret Key',
        placeholder: 'sk_live_... or sk_test_...',
        hint: 'Found in Dashboard → Developers → API Keys (click to reveal)'
      },
      webhook_secret: {
        label: 'Webhook Signing Secret',
        placeholder: 'whsec_...',
        hint: 'Found in Dashboard → Developers → Webhooks → Select endpoint → Signing secret'
      }
    },
    instructions: [
      'Log in to your Stripe Dashboard at dashboard.stripe.com',
      'Navigate to Developers → API Keys',
      'Copy your Publishable Key (starts with pk_)',
      'Click "Reveal" to copy your Secret Key (starts with sk_)',
      'For webhooks, go to Developers → Webhooks → Add endpoint',
      'Set the endpoint URL to: {webhookUrl}/api/webhooks/stripe',
      'Select events: checkout.session.completed, payment_intent.succeeded, payment_intent.payment_failed',
      'After creating, click on the endpoint and copy the Signing Secret (starts with whsec_)'
    ]
  },
  RAZORPAY: {
    label: 'Razorpay',
    description: 'Accept payments in India with Razorpay',
    dashboardUrl: 'https://dashboard.razorpay.com/app/keys',
    docsUrl: 'https://razorpay.com/docs/api/',
    fields: {
      api_key: {
        label: 'Key ID',
        placeholder: 'rzp_live_... or rzp_test_...',
        hint: 'Found in Dashboard → Settings → API Keys'
      },
      api_secret: {
        label: 'Key Secret',
        placeholder: 'Your Razorpay Key Secret',
        hint: 'Shown only once when generating API keys. Generate new keys if lost.'
      },
      webhook_secret: {
        label: 'Webhook Secret',
        placeholder: 'Your webhook secret',
        hint: 'Found in Dashboard → Settings → Webhooks → Secret'
      }
    },
    instructions: [
      'Log in to your Razorpay Dashboard at dashboard.razorpay.com',
      'Navigate to Settings → API Keys',
      'Click "Generate Key" to create new API keys',
      'Copy the Key ID (starts with rzp_) and Key Secret immediately',
      'Important: The Key Secret is shown only once!',
      'For webhooks, go to Settings → Webhooks → Add New Webhook',
      'Set the Webhook URL to: {webhookUrl}/api/webhooks/razorpay',
      'Select events: payment.captured, payment.failed, order.paid',
      'Copy the Secret shown after creating the webhook'
    ]
  },
  PAYPAL: {
    label: 'PayPal',
    description: 'Accept PayPal payments globally',
    dashboardUrl: 'https://developer.paypal.com/dashboard/applications',
    docsUrl: 'https://developer.paypal.com/docs/api/overview/',
    fields: {
      api_key: {
        label: 'Client ID',
        placeholder: 'Your PayPal Client ID',
        hint: 'Found in Developer Dashboard → My Apps & Credentials → Select App'
      },
      api_secret: {
        label: 'Client Secret',
        placeholder: 'Your PayPal Client Secret',
        hint: 'Found in Developer Dashboard → My Apps & Credentials → Select App → Show Secret'
      },
      webhook_secret: {
        label: 'Webhook ID',
        placeholder: 'Your PayPal Webhook ID',
        hint: 'Found in Developer Dashboard → My Apps & Credentials → Webhooks'
      }
    },
    instructions: [
      'Log in to PayPal Developer Dashboard at developer.paypal.com',
      'Navigate to My Apps & Credentials',
      'Select your app or create a new one',
      'Copy the Client ID from the app details',
      'Click "Show" under Secret to reveal and copy the Client Secret',
      'Scroll down to Webhooks and click "Add Webhook"',
      'Set the Webhook URL to: {webhookUrl}/api/webhooks/paypal',
      'Select events: PAYMENT.CAPTURE.COMPLETED, PAYMENT.CAPTURE.DENIED',
      'After creating, copy the Webhook ID'
    ]
  }
}

interface PaymentGatewayFormPageProps {
  mode?: 'create' | 'edit'
}

export default function PaymentGatewayFormPage({ mode = 'create' }: PaymentGatewayFormPageProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const isEditMode = mode === 'edit' && id

  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({})
  const [formData, setFormData] = useState<CreatePaymentGatewaySettingsRequest>({
    gateway_type: 'STRIPE',
    is_enabled: false,
    is_test_mode: true,
    api_key: '',
    api_secret: '',
    webhook_secret: '',
    display_name: '',
  })

  // Fetch existing gateway for edit mode
  const { data: existingGateway, isLoading: isLoadingGateway } = useQuery({
    queryKey: ['payment-gateway', id],
    queryFn: () => paymentGatewaysApi.getById(id!),
    enabled: !!isEditMode,
  })

  // Fetch all gateways to check which types are already configured
  const { data: allGateways = [] } = useQuery({
    queryKey: ['payment-gateways'],
    queryFn: paymentGatewaysApi.getAll,
  })

  // Get available gateway types (not already configured)
  const configuredTypes = allGateways.map(g => g.gateway_type)
  const availableTypes = (Object.keys(GATEWAY_CONFIG) as GatewayType[]).filter(
    type => !configuredTypes.includes(type) || (isEditMode && existingGateway?.gateway_type === type)
  )

  // Set form data when editing
  useEffect(() => {
    if (existingGateway) {
      setFormData({
        gateway_type: existingGateway.gateway_type,
        is_enabled: existingGateway.is_enabled,
        is_test_mode: existingGateway.is_test_mode,
        api_key: '',
        api_secret: '',
        webhook_secret: '',
        display_name: existingGateway.display_name || '',
      })
    }
  }, [existingGateway])

  // Set first available type for new gateways
  useEffect(() => {
    if (!isEditMode && availableTypes.length > 0 && !availableTypes.includes(formData.gateway_type)) {
      setFormData(prev => ({ ...prev, gateway_type: availableTypes[0] }))
    }
  }, [availableTypes, isEditMode, formData.gateway_type])

  const createMutation = useMutation({
    mutationFn: paymentGatewaysApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-gateways'] })
      toast.success(t('payments.gatewayCreated', 'Payment gateway configured successfully'))
      navigate('/settings/payment-gateways')
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('payments.createError', 'Failed to configure gateway'))),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => paymentGatewaysApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-gateways'] })
      toast.success(t('payments.gatewayUpdated', 'Payment gateway updated successfully'))
      navigate('/settings/payment-gateways')
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('payments.updateError', 'Failed to update gateway'))),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (isEditMode) {
      const updateData: any = {
        is_enabled: formData.is_enabled,
        is_test_mode: formData.is_test_mode,
        display_name: formData.display_name || undefined,
      }
      if (formData.api_key) updateData.api_key = formData.api_key
      if (formData.api_secret) updateData.api_secret = formData.api_secret
      if (formData.webhook_secret) updateData.webhook_secret = formData.webhook_secret
      updateMutation.mutate({ id: id!, data: updateData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const currentConfig = GATEWAY_CONFIG[formData.gateway_type]
  const webhookUrl = window.location.origin

  if (isEditMode && isLoadingGateway) {
    return <div className="flex items-center justify-center h-64">Loading...</div>
  }

  if (!isEditMode && availableTypes.length === 0) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>All Gateways Configured</AlertTitle>
          <AlertDescription>
            All available payment gateways have already been configured.
          </AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => navigate('/settings/payment-gateways')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Payment Gateways
        </Button>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-6 space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/settings/payment-gateways')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">
            {isEditMode
              ? t('payments.editGateway', 'Edit Payment Gateway')
              : t('payments.addGateway', 'Add Payment Gateway')}
          </h1>
          <p className="text-muted-foreground">
            {currentConfig.description}
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Instructions Panel */}
        <Card className="lg:order-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5" />
              Setup Instructions
            </CardTitle>
            <CardDescription>
              Follow these steps to get your {currentConfig.label} API credentials
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ol className="list-decimal list-inside space-y-2 text-sm">
              {currentConfig.instructions.map((instruction, index) => (
                <li key={index} className="text-muted-foreground">
                  {instruction.replace('{webhookUrl}', webhookUrl)}
                </li>
              ))}
            </ol>

            <div className="flex flex-col gap-2 pt-4 border-t">
              <Button variant="outline" size="sm" asChild>
                <a href={currentConfig.dashboardUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open {currentConfig.label} Dashboard
                </a>
              </Button>
              <Button variant="ghost" size="sm" asChild>
                <a href={currentConfig.docsUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Documentation
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Form Panel */}
        <Card className="lg:order-1">
          <CardHeader>
            <CardTitle>Gateway Configuration</CardTitle>
            <CardDescription>
              {isEditMode
                ? 'Update your gateway settings. Leave credential fields empty to keep existing values.'
                : 'Enter your API credentials to configure this payment gateway.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Gateway Type Selection (only for new) */}
              {!isEditMode && availableTypes.length > 1 && (
                <div className="space-y-2">
                  <Label htmlFor="gateway_type">Gateway Type</Label>
                  <Select
                    value={formData.gateway_type}
                    onValueChange={(value: GatewayType) => setFormData({ ...formData, gateway_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a gateway" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {GATEWAY_CONFIG[type].label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Display Name */}
              <div className="space-y-2">
                <Label htmlFor="display_name">Display Name (Optional)</Label>
                <Input
                  id="display_name"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  placeholder={`e.g., Primary ${currentConfig.label} Account`}
                />
              </div>

              {/* API Key */}
              <div className="space-y-2">
                <Label htmlFor="api_key">
                  {currentConfig.fields.api_key.label}
                  {isEditMode && <span className="text-muted-foreground text-xs ml-2">(leave empty to keep current)</span>}
                </Label>
                <div className="relative">
                  <Input
                    id="api_key"
                    type={showSecrets.api_key ? 'text' : 'password'}
                    value={formData.api_key}
                    onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                    placeholder={isEditMode ? '••••••••' : currentConfig.fields.api_key.placeholder}
                    required={!isEditMode}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0"
                    onClick={() => setShowSecrets({ ...showSecrets, api_key: !showSecrets.api_key })}
                  >
                    {showSecrets.api_key ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">{currentConfig.fields.api_key.hint}</p>
              </div>

              {/* API Secret */}
              <div className="space-y-2">
                <Label htmlFor="api_secret">
                  {currentConfig.fields.api_secret.label}
                  {isEditMode && <span className="text-muted-foreground text-xs ml-2">(leave empty to keep current)</span>}
                </Label>
                <div className="relative">
                  <Input
                    id="api_secret"
                    type={showSecrets.api_secret ? 'text' : 'password'}
                    value={formData.api_secret}
                    onChange={(e) => setFormData({ ...formData, api_secret: e.target.value })}
                    placeholder={isEditMode ? '••••••••' : currentConfig.fields.api_secret.placeholder}
                    required={!isEditMode}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0"
                    onClick={() => setShowSecrets({ ...showSecrets, api_secret: !showSecrets.api_secret })}
                  >
                    {showSecrets.api_secret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">{currentConfig.fields.api_secret.hint}</p>
              </div>

              {/* Webhook Secret */}
              <div className="space-y-2">
                <Label htmlFor="webhook_secret">
                  {currentConfig.fields.webhook_secret.label}
                  {isEditMode && <span className="text-muted-foreground text-xs ml-2">(leave empty to keep current)</span>}
                </Label>
                <div className="relative">
                  <Input
                    id="webhook_secret"
                    type={showSecrets.webhook_secret ? 'text' : 'password'}
                    value={formData.webhook_secret}
                    onChange={(e) => setFormData({ ...formData, webhook_secret: e.target.value })}
                    placeholder={isEditMode ? '••••••••' : currentConfig.fields.webhook_secret.placeholder}
                    required={!isEditMode}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0"
                    onClick={() => setShowSecrets({ ...showSecrets, webhook_secret: !showSecrets.webhook_secret })}
                  >
                    {showSecrets.webhook_secret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">{currentConfig.fields.webhook_secret.hint}</p>
              </div>

              {/* Test Mode Toggle */}
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label htmlFor="is_test_mode">Test Mode</Label>
                  <p className="text-sm text-muted-foreground">Use test/sandbox credentials for testing</p>
                </div>
                <Switch
                  id="is_test_mode"
                  checked={formData.is_test_mode}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_test_mode: checked })}
                />
              </div>

              {/* Enable Gateway Toggle */}
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label htmlFor="is_enabled">Enable Gateway</Label>
                  <p className="text-sm text-muted-foreground">Allow payments through this gateway</p>
                </div>
                <Switch
                  id="is_enabled"
                  checked={formData.is_enabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_enabled: checked })}
                />
              </div>

              {/* Form Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/settings/payment-gateways')}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="flex-1"
                >
                  {(createMutation.isPending || updateMutation.isPending)
                    ? 'Saving...'
                    : isEditMode ? 'Update Gateway' : 'Add Gateway'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

